import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp, Loader2, Download, Package, Award, CreditCard, History, Truck, CheckCircle, XCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import type { OrderItem } from '../types/orders';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

// Import the OrderStatus enum from Warehouse
import { OrderStatus } from './Warehouse';

// Update the Order interface to include status
interface Order {
  id: string;
  created_at: string;
  visionary_id: number | null;
  staff_id: number | null;
  total_amount: number;
  points_used: number;
  points_earned: number;
  status: OrderStatus;
  staff: {
    id: number;
    full_name: string;
  } | null;
  order_items: OrderItem[];
}

export default function Orders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<{orderId: string, action: string} | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundOrderId, setRefundOrderId] = useState<string | null>(null);
  const [refundReason, setRefundReason] = useState<string>('');

  // Get visionary details from the user context
  const visionary = user?.visionary_details ? JSON.parse(user.visionary_details as string) : null;

  useEffect(() => {
    if (visionary) {
      fetchOrders();
    }
  }, [visionary]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          staff:staff_id (
            id,
            full_name
          ),
          order_items (
            id,
            product_id,
            quantity,
            price_at_time,
            products (
              name,
              platform,
              image_url
            )
          )
        `)
        .eq('visionary_id', visionary.membership_number)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Default status to 'processing' for any orders without a status (backward compatibility)
      const ordersWithStatus = (data || []).map(order => ({
        ...order,
        status: order.status || OrderStatus.PROCESSING
      }));
      
      setOrders(ordersWithStatus);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandOrder = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Format the status text - replace underscore with space and capitalize
  const formatStatus = (status: string) => {
    if (status === OrderStatus.RETURN_INITIATED) {
      return 'Return Initiated';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Get status badge style based on status
  const getStatusBadgeStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case OrderStatus.DISPATCHED:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case OrderStatus.COMPLETE:
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case OrderStatus.CANCELLED:
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case OrderStatus.RETURN_INITIATED:
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case OrderStatus.REFUNDED:
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: OrderStatus }) => {
    // Define style and icon based on status
    let bgColor = '';
    let textColor = '';
    let icon = null;
    
    switch (status) {
      case OrderStatus.PROCESSING:
        bgColor = 'bg-purple-500/20';
        textColor = 'text-purple-400';
        icon = <History className="w-4 h-4 mr-1.5" />;
        break;
      case OrderStatus.DISPATCHED:
        bgColor = 'bg-blue-500/20';
        textColor = 'text-blue-400';
        icon = <Truck className="w-4 h-4 mr-1.5" />;
        break;
      case OrderStatus.COMPLETE:
        bgColor = 'bg-green-500/20';
        textColor = 'text-green-400';
        icon = <CheckCircle className="w-4 h-4 mr-1.5" />;
        break;
      case OrderStatus.CANCELLED:
        bgColor = 'bg-red-500/20';
        textColor = 'text-red-400';
        icon = <XCircle className="w-4 h-4 mr-1.5" />;
        break;
      case OrderStatus.RETURN_INITIATED:
        bgColor = 'bg-amber-500/20';
        textColor = 'text-amber-400';
        icon = <Package className="w-4 h-4 mr-1.5" />;
        break;
      case OrderStatus.REFUNDED:
        bgColor = 'bg-orange-500/20';
        textColor = 'text-orange-400';
        icon = <AlertTriangle className="w-4 h-4 mr-1.5" />;
        break;
      default:
        bgColor = 'bg-gray-500/20';
        textColor = 'text-gray-400';
        icon = <History className="w-4 h-4 mr-1.5" />;
    }
    
    return (
      <div className={`flex items-center ${bgColor} ${textColor} px-4 py-3 rounded-lg border border-${textColor.replace('text-', 'border-')}/30`}>
        {icon}
        <span className="font-medium">
          {formatStatus(status)}
        </span>
      </div>
    );
  };

  // Return instructions component
  const ReturnInstructions = () => {
    return (
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-lg p-4 my-6">
        <div className="flex items-start gap-3">
          <Package className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-amber-400 font-medium">Return Instructions</h3>
            <p className="text-white/80 text-sm mt-2">
              Please return your item(s) to the address below within 14 days to process your refund.
            </p>
            <div className="mt-3 pl-2 border-l-2 border-amber-500/30">
              <p className="text-white/80 text-sm">Big Vision Games Returns</p>
              <p className="text-white/80 text-sm">190 Cathedral Street</p>
              <p className="text-white/80 text-sm">Glasgow, UK G4 0RF</p>
            </div>
            <p className="text-white/60 text-xs mt-3">
              Your refund will be processed once we have received and verified your return.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const generateInvoice = (order: Order) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header with company details
    doc.setFontSize(28);
    doc.setTextColor(88, 28, 135); // Purple color for brand
    doc.text('Big Vision Games', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128); // Gray for secondary text
    doc.text('Gaming Beyond Boundaries', pageWidth / 2, 32, { align: 'center' });
    
    // Divider line
    doc.setDrawColor(88, 28, 135);
    doc.setLineWidth(0.5);
    doc.line(20, 38, pageWidth - 20, 38);

    // Invoice details section
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text('Order Confirmation', 20, 50);

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    // Left column - Order details
    doc.text('Order Details:', 20, 65);
    doc.text(`Order Number: ${order.id}`, 20, 72);
    doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 20, 79);
    doc.text(`Status: ${order.status}`, 20, 86);
    if (order.staff) {
      doc.text(`Staff Member: ${order.staff.full_name}`, 20, 93);
      doc.text(`Staff ID: ${order.staff.id}`, 20, 100);
    } else {
      doc.text('Online Order', 20, 93);
    }

    // Right column - Company details
    const rightColumn = pageWidth - 80;
    doc.text('Company Details:', rightColumn, 65);
    doc.text('Big Vision Games Ltd.', rightColumn, 72);
    doc.text('190 Cathedral Street', rightColumn, 79);
    doc.text('Glasgow, UK G4 0RF', rightColumn, 86);
    doc.text('Tel: 0141 321 1056', rightColumn, 93);

    // Customer section
    let yPos = 110;
    if (visionary) {
      doc.setFontSize(12);
      doc.setTextColor(88, 28, 135);
      doc.text('Customer Information', 20, yPos);
      
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      yPos += 8;
      doc.text(`${visionary.name}`, 20, yPos);
      yPos += 7;
      doc.text(`${visionary.email}`, 20, yPos);
      yPos += 7;
      doc.text(`${visionary.address}`, 20, yPos);
      yPos += 7;
      doc.text(`Visionary ID: ${visionary.membership_number}`, 20, yPos);
      yPos += 7;
      doc.text(`Tel: ${visionary.telephone}`, 20, yPos);
      yPos += 15;
    } else {
      yPos = 120;
    }

    // Items table with enhanced styling
    const tableData = order.order_items.map(item => [
      item.product_id.toString(),
      `${item.products.name}${item.products.platform ? ` (${item.products.platform})` : ''}`,
      item.quantity.toString(),
      `£${item.price_at_time.toFixed(2)}`,
      `£${(item.price_at_time * item.quantity).toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Product ID', 'Item Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 5,
      },
      headStyles: {
        fillColor: [88, 28, 135],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Product ID
        1: { cellWidth: 'auto' }, // Item Description
        2: { cellWidth: 20, halign: 'center' }, // Quantity
        3: { cellWidth: 30, halign: 'right' }, // Unit Price
        4: { cellWidth: 30, halign: 'right' }, // Total
      },
      margin: { bottom: 60 }, // Leave space for totals at bottom
      didDrawPage: (data) => {
        // Add page number at the bottom of each page
        const pageNumber = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${pageNumber}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      }
    });

    // Get the final Y position after the table (on whatever page it ended)
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    const currentPage = doc.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.height;

    // Check if we need to add a new page for totals
    if (finalY > pageHeight - 60) {
      doc.addPage();
    }

    // Add a subtle line above totals
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(pageWidth - 100, finalY - 5, pageWidth - 20, finalY - 5);

    // Points Information
    if (order.points_used > 0 || order.points_earned > 0) {
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text('Points Summary:', pageWidth - 100, finalY);
      doc.text(`Points Used:`, pageWidth - 100, finalY + 7);
      doc.text(`${order.points_used}`, pageWidth - 30, finalY + 7, { align: 'right' });
      doc.text(`Points Earned:`, pageWidth - 100, finalY + 14);
      doc.text(`${order.points_earned}`, pageWidth - 30, finalY + 14, { align: 'right' });
      doc.text(`Savings:`, pageWidth - 100, finalY + 21);
      doc.text(`£${(order.points_used / 100).toFixed(2)}`, pageWidth - 30, finalY + 21, { align: 'right' });
      
      // Final total with bold styling
      doc.setFontSize(12);
      doc.setFont('Helvetica', 'bold');
      doc.text('Total Amount:', pageWidth - 100, finalY + 32);
      doc.text(`£${order.total_amount.toFixed(2)}`, pageWidth - 30, finalY + 32, { align: 'right' });
      
      // Footer note
      doc.setFontSize(9);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(`A copy of this invoice has been emailed to ${visionary.email}`, pageWidth / 2, finalY + 45, { align: 'center' });
    } else {
      // Simple total for non-visionary customers
      doc.setFontSize(12);
      doc.setFont('Helvetica', 'bold');
      doc.text('Total Amount:', pageWidth - 100, finalY + 7);
      doc.text(`£${order.total_amount.toFixed(2)}`, pageWidth - 30, finalY + 7, { align: 'right' });
    }

    // Footer
    const footerY = doc.internal.pageSize.height - 20;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for shopping with Big Vision Games', pageWidth / 2, footerY, { align: 'center' });

    doc.save(`BVG-Invoice-${order.id}.pdf`);
  };

  // Cancel order function
  const cancelOrder = async (orderId: string) => {
    try {
      setProcessingAction({ orderId, action: 'cancel' });
      
      // Find the order in the existing state
      const orderToCancel = orders.find(order => order.id === orderId);
      if (!orderToCancel) throw new Error("Order not found");
      
      console.log('Cancelling order:', orderId, 'with points:', orderToCancel.points_earned);
      
      // Update order status and reset points_earned to 0 in database
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: OrderStatus.CANCELLED,
          points_earned: 0 
        })
        .eq('id', orderId);
      
      if (updateError) {
        console.error('Error updating order status:', updateError);
        throw new Error(`Failed to update order status: ${updateError.message}`);
      }
      
      console.log('Order status updated successfully');
      
      // Remove the earned points from the customer's account (if any points were earned)
      if (orderToCancel.points_earned > 0 && visionary) {
        console.log('Updating customer points, removing:', orderToCancel.points_earned);
        const { error: pointsError } = await supabase.rpc('update_visionary_points', {
          p_membership_number: visionary.membership_number,
          p_points_change: -orderToCancel.points_earned // Subtract the earned points
        });
        
        if (pointsError) {
          console.error('Error updating visionary points:', pointsError);
          // Don't throw here - points adjustment is not critical for order cancellation
          console.warn('Points adjustment failed but continuing with cancellation');
        } else {
          console.log('Customer points updated successfully');
        }
      }
      
      // Restore inventory for each item
      console.log('Restoring inventory for', orderToCancel.order_items.length, 'items');
      for (const item of orderToCancel.order_items) {
        try {
          // First get the current product stock
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('stock_quantity')
            .eq('id', item.product_id)
            .single();
          
          if (productError) {
            console.error(`Error fetching product ${item.product_id} stock:`, productError);
            continue; // Skip this item but continue with others
          }
          
          // Then update with the current stock + returned quantity
          const { error: stockError } = await supabase
            .from('products')
            .update({ 
              stock_quantity: productData.stock_quantity + item.quantity 
            })
            .eq('id', item.product_id);
          
          if (stockError) {
            console.error(`Error updating stock for product ${item.product_id}:`, stockError);
            // Continue with other items even if one fails
          } else {
            console.log(`Restored ${item.quantity} units for product ${item.product_id}`);
          }
        } catch (itemError) {
          console.error(`Error processing item ${item.product_id}:`, itemError);
          // Continue with other items
        }
      }
      
      console.log('Inventory restoration completed');
      
      // Refresh orders data from database to ensure UI reflects changes
      try {
        await fetchOrders();
        console.log('Orders data refreshed successfully');
      } catch (fetchError) {
        console.error('Error refreshing orders:', fetchError);
        // Don't throw - the cancellation succeeded even if refresh failed
      }
      
      setActionMessage({ 
        type: 'success', 
        message: `Your order has been cancelled. Inventory has been restored${orderToCancel.points_earned > 0 ? ' and loyalty points have been adjusted' : ''}.` 
      });
      
    } catch (err) {
      console.error('Error cancelling order:', err);
      setActionMessage({ 
        type: 'error', 
        message: 'Failed to cancel your order. Please try again or contact support.' 
      });
    } finally {
      setProcessingAction(null);
      setTimeout(() => setActionMessage(null), 5000); // Clear message after 5 seconds
    }
  };

  // Request refund function - now shows modal first
  const initiateRefund = (orderId: string) => {
    setRefundOrderId(orderId);
    setShowRefundModal(true);
  };

  // Process refund after confirmation
  const confirmRefund = async () => {
    if (!refundOrderId) return;
    
    try {
      setProcessingAction({ orderId: refundOrderId, action: 'refund' });
      setShowRefundModal(false);
      
      // Update order status in database to RETURN_INITIATED instead of REFUNDED
      // Also store the return reason
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: OrderStatus.RETURN_INITIATED,
          return_reason: refundReason
        })
        .eq('id', refundOrderId);
      
      if (error) throw error;
      
      // Update local state
      setOrders(prevOrders => prevOrders.map(order => 
        order.id === refundOrderId ? { 
          ...order, 
          status: OrderStatus.RETURN_INITIATED,
          return_reason: refundReason
        } : order
      ));
      
      setActionMessage({ 
        type: 'success', 
        message: 'Your return has been initiated. Please return your item(s) to process the refund.' 
      });
      
    } catch (err) {
      console.error('Error initiating return:', err);
      setActionMessage({ 
        type: 'error', 
        message: 'Failed to process your return request. Please try again or contact support.' 
      });
    } finally {
      setProcessingAction(null);
      setRefundOrderId(null);
      setRefundReason(''); // Reset the reason field
      setTimeout(() => setActionMessage(null), 5000); // Clear message after 5 seconds
    }
  };

  // Cancel refund process
  const cancelRefund = () => {
    setShowRefundModal(false);
    setRefundOrderId(null);
  };

  // Get order icon based on status
  const getOrderIcon = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return <History className="w-6 h-6" />;
      case OrderStatus.DISPATCHED:
        return <Truck className="w-6 h-6" />;
      case OrderStatus.COMPLETE:
        return <CheckCircle className="w-6 h-6" />;
      case OrderStatus.CANCELLED:
        return <XCircle className="w-6 h-6" />;
      case OrderStatus.RETURN_INITIATED:
        return <Package className="w-6 h-6" />;
      case OrderStatus.REFUNDED:
        return <RotateCcw className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  // Get icon container style based on status
  const getIconContainerStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return 'bg-purple-600/20 text-purple-400';
      case OrderStatus.DISPATCHED:
        return 'bg-blue-600/20 text-blue-400';
      case OrderStatus.COMPLETE:
        return 'bg-green-600/20 text-green-400';
      case OrderStatus.CANCELLED:
        return 'bg-red-600/20 text-red-400';
      case OrderStatus.RETURN_INITIATED:
        return 'bg-amber-600/20 text-amber-400';
      case OrderStatus.REFUNDED:
        return 'bg-orange-600/20 text-orange-400';
      default:
        return 'bg-purple-600/20 text-purple-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md p-8 rounded-2xl backdrop-blur-xl bg-white/20 shadow-2xl border border-white/10">
            <h3 className="text-xl text-white font-thin flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-white" />
              Return Request
            </h3>
            <p className="text-white/80 mb-4">
              To process your refund, please return the item(s) to our warehouse within 14 days. Once we receive and verify the returned item(s), your refund will be processed.
            </p>
            
            {/* Return Reason Field */}
            <div className="mb-4">
              <label htmlFor="returnReason" className="block text-white/80 mb-2">
                Reason for Return (Required)
              </label>
              <textarea
                id="returnReason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Please explain why you're returning this item..."
                className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all min-h-[100px]"
                required
              />
            </div>
            
            <div className="p-4 text-sm text-amber-200 bg-amber-500/20 border border-amber-500/20 rounded-lg mb-6">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Return Instructions:</p>
                  <p className="mt-1">Please ship your item(s) to:</p>
                  <p className="mt-1">Big Vision Games Returns</p>
                  <p>190 Cathedral Street</p>
                  <p>Glasgow, UK G4 0RF</p>
                </div>
              </div>
            </div>
            <div className="space-y-3 mt-6">
              <button
                onClick={confirmRefund}
                disabled={!refundReason.trim()} // Disable button if reason is empty
                className={`w-full py-3 px-4 ${!refundReason.trim() ? 'bg-purple-600/50 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'} text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent`}
              >
                Confirm Return
              </button>
              <button
                onClick={cancelRefund}
                className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Background Design */}
      <div className="fixed inset-0 z-0">
        {/* Base background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/images/back2.png")' }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/40 via-purple-900/50 to-purple-950/60" />
        
        {/* Glass effect overlay */}
        <div className="absolute inset-0 backdrop-blur-[30px]" />
      </div>

      <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto relative z-10">
        <div className="w-full max-w-[1400px] min-w-[800px] mx-auto p-8">
          {/* Action Message */}
          {actionMessage && (
            <div className={`mb-4 p-4 rounded-lg flex items-center ${
              actionMessage.type === 'success' 
                ? 'bg-green-900/20 border border-green-500/30 text-green-400' 
                : 'bg-red-900/20 border border-red-500/30 text-red-400'
            }`}>
              {actionMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              )}
              <p>{actionMessage.message}</p>
              <button 
                onClick={() => setActionMessage(null)} 
                className="ml-auto text-white/50 hover:text-white"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-white" />
                  <h1 className="text-2xl font-bold text-white">My Orders</h1>
                </div>
                <p className="mt-2 text-white/70">View and manage your order history</p>
              </div>

              {/* Quick Stats */}
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <h3 className="text-sm font-medium text-white/70">Total Orders</h3>
                <p className="text-3xl font-bold text-white mt-1">{orders.length}</p>
                <p className="text-sm text-white/50 mt-1">Lifetime purchases</p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <h3 className="text-sm font-medium text-white/70">Points Earned</h3>
                <p className="text-3xl font-bold text-white mt-1">
                  {orders.reduce((sum, order) => sum + (order.points_earned || 0), 0)}
                </p>
                <p className="text-sm text-white/50 mt-1">From all orders</p>
              </div>

              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <h3 className="text-sm font-medium text-white/70">Total Spent</h3>
                <p className="text-3xl font-bold text-white mt-1">
                  £{orders.reduce((sum, order) => sum + order.total_amount, 0).toFixed(2)}
                </p>
                <p className="text-sm text-white/50 mt-1">Lifetime value</p>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-[2fr_1fr] gap-6">
              {/* Orders List */}
              <div className="min-w-0">
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
                  </div>
                  
                  <div className="max-h-[calc(100vh-380px)] overflow-y-auto scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-white/5 hover:scrollbar-thumb-purple-500/70">
                    <div className="p-6">
                      {orders.length > 0 ? (
                        <div className="space-y-4">
                          {orders.map((order) => (
                            <div 
                              key={order.id} 
                              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden"
                            >
                              {/* Order Header */}
                              <div 
                                className="p-6 flex items-center justify-between cursor-pointer border-b border-white/10"
                                onClick={() => handleExpandOrder(order.id)}
                              >
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                  <div className={`flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0 ${getIconContainerStyle(order.status)}`}>
                                    {getOrderIcon(order.status)}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-lg font-medium text-white truncate">Order #{order.id}</h3>
                                    <p className="text-white/50 text-sm">
                                      {new Date(order.created_at).toLocaleDateString()} at{' '}
                                      {new Date(order.created_at).toLocaleTimeString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                                  <div className={`px-3 py-1 rounded-full text-xs border whitespace-nowrap flex-shrink-0 ${getStatusBadgeStyle(order.status)}`}>
                                    {formatStatus(order.status)}
                                  </div>
                                  <div className="text-right flex-none">
                                    <p className="text-lg font-medium text-white">£{order.total_amount.toFixed(2)}</p>
                                    <p className="text-white/50 text-sm">
                                      {order.order_items.length} item{order.order_items.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 flex-shrink-0 ml-2">
                                    {expandedOrder === order.id ? (
                                      <ChevronUp className="w-5 h-5 text-white" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-white" />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Expanded Order Details */}
                              {expandedOrder === order.id && (
                                <div className="p-6 space-y-6">
                                  {/* Order Status */}
                                  <StatusBadge status={order.status} />
                                  
                                  {/* Show return instructions for RETURN_INITIATED status */}
                                  {order.status === OrderStatus.RETURN_INITIATED && <ReturnInstructions />}
                                  
                                  {/* Order Items */}
                                  <div className="space-y-4">
                                    {order.order_items.map((item: OrderItem) => (
                                      <div
                                        key={item.id}
                                        className="flex items-center gap-4 p-4 bg-white/5 rounded-lg"
                                      >
                                        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-white/5">
                                          <img
                                            src={item.products.image_url}
                                            alt={item.products.name}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="text-white font-medium truncate">
                                            {item.products.name}
                                          </h4>
                                          <div className="flex items-center gap-2 text-sm text-white/70">
                                            {item.products.platform && (
                                              <span className="px-2 py-0.5 bg-white/10 rounded">
                                                {item.products.platform}
                                              </span>
                                            )}
                                            <span>Quantity: {item.quantity}</span>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-white font-medium">
                                            £{(item.price_at_time * item.quantity).toFixed(2)}
                                          </p>
                                          <p className="text-sm text-white/50">
                                            £{item.price_at_time.toFixed(2)} each
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Order Summary */}
                                  <div className="bg-white/5 rounded-lg p-6 space-y-3">
                                    <div className="flex justify-between text-white/80">
                                      <span>Subtotal</span>
                                      <span>£{order.total_amount.toFixed(2)}</span>
                                    </div>
                                    {order.points_used > 0 && (
                                      <div className="flex justify-between text-white/80">
                                        <span>Points Used</span>
                                        <span>-{order.points_used} points</span>
                                      </div>
                                    )}
                                    {order.points_earned > 0 && (
                                      <div className="flex justify-between text-white/80">
                                        <span>Points Earned</span>
                                        <span>+{order.points_earned} points</span>
                                      </div>
                                    )}
                                    <div className="pt-3 border-t border-white/10">
                                      <div className="flex justify-between items-center">
                                        <div className="flex justify-between text-lg font-medium text-white">
                                          <span>Total</span>
                                          <span className="ml-4">£{order.total_amount.toFixed(2)}</span>
                                        </div>
                                        <button
                                          onClick={() => generateInvoice(order)}
                                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                                        >
                                          <Download className="w-4 h-4" />
                                          Download Invoice
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Order Actions */}
                                  {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.REFUNDED && order.status !== OrderStatus.RETURN_INITIATED && (
                                    <div className="bg-white/5 rounded-lg p-6 space-y-4">
                                      <h3 className="text-white font-medium">Order Actions</h3>
                                      
                                      <div className="text-white/60 text-sm rounded-lg bg-purple-500/10 p-4 border border-purple-500/20">
                                        <AlertTriangle className="w-4 h-4 text-purple-400 inline-block mr-2" />
                                        Note: Loyalty points will not be returned if an order is cancelled or refunded.
                                      </div>
                                      
                                      <div className="flex flex-col sm:flex-row gap-4">
                                        {/* Cancel Order Button */}
                                        <div className="relative flex-1" onMouseEnter={() => order.status !== OrderStatus.PROCESSING ? setShowTooltip(order.id) : null} onMouseLeave={() => setShowTooltip(null)}>
                                          <button
                                            onClick={() => cancelOrder(order.id)}
                                            disabled={order.status !== OrderStatus.PROCESSING || processingAction?.orderId === order.id}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                                              order.status === OrderStatus.PROCESSING 
                                                ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                : 'bg-red-600/30 text-white/50 cursor-not-allowed'
                                            }`}
                                          >
                                            {processingAction?.orderId === order.id && processingAction?.action === 'cancel' ? (
                                              <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                              <XCircle className="w-4 h-4" />
                                            )}
                                            Cancel Order
                                          </button>
                                          
                                          {/* Tooltip for disabled cancel button */}
                                          {showTooltip === order.id && order.status !== OrderStatus.PROCESSING && (
                                            <div className="absolute bottom-full mb-2 left-0 right-0 bg-black/80 backdrop-blur-md text-white p-3 rounded-lg text-sm z-10">
                                              Dispatched or completed orders cannot be cancelled. You can request a refund instead.
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Request Refund Button */}
                                        <button
                                          onClick={() => initiateRefund(order.id)}
                                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                                            (order.status === OrderStatus.DISPATCHED || order.status === OrderStatus.COMPLETE)
                                              ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                                              : 'bg-amber-600/30 text-white/50 cursor-not-allowed'
                                          }`}
                                        >
                                          {processingAction?.orderId === order.id && processingAction?.action === 'refund' ? (
                                            <>
                                              <Loader2 className="w-4 h-4 animate-spin" />
                                              Processing...
                                            </>
                                          ) : (
                                            <>
                                              <RotateCcw className="w-4 h-4" />
                                              Request Refund
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 space-y-4">
                          <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto">
                            <Package className="w-10 h-10 text-white/30" />
                          </div>
                          <div>
                            <p className="text-white/70 text-lg">No orders yet</p>
                            <p className="text-white/50 text-sm mt-1">Start shopping to see your orders here</p>
                          </div>
                          <button 
                            onClick={() => navigate('/shop')}
                            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors mt-4"
                          >
                            Browse Products
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="min-w-0 space-y-6">
                {/* Membership Benefits */}
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white">Membership Benefits</h2>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <Award className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Earn Points</h3>
                        <p className="text-white/70 text-sm mt-1">Get 1 point for every £1 spent</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Redeem Rewards</h3>
                        <p className="text-white/70 text-sm mt-1">Use points for discounts on future purchases</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <History className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Early Access</h3>
                        <p className="text-white/70 text-sm mt-1">Get early access to new releases and sales</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Need Help? */}
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-2">Need Help?</h2>
                  <p className="text-white/70 text-sm mb-4">Our support team is here to assist you with your orders</p>
                  <button className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 
